import { useState, useEffect, Suspense } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { useMutation } from '@tanstack/react-query';
import { BUTTON_THEMES } from 'ht-styleguide';

/* Apis */
import { credits } from '@api/apis';

/* Stores */
import useNotice from '@hooks/useNotice';

/* Utils */
import { currencyFormatter } from '@utils/formatting';
import { Logger } from '@utils/logger';

import styles from './claimStoreCreditsModal.module.scss';

/* Dynamic */
const LazySuccessLottie = dynamic(() => import('./ClaimStoreCreditsModal.SuccessLottie'));
const Modal = dynamic(() => import('ht-styleguide').then(mod => mod.Modal), { ssr: false });
const Button = dynamic(() => import('ht-styleguide').then(mod => mod.Button), { ssr: false });

type TClaimSuccessfulContent = {
  close: BaseAnyFunction;
  creditInfo: {
    amount: number;
  };
};

const CREDIT_LABEL = 'HelloTech Cash';

const ClaimSuccessfulContent = ({ creditInfo, close }: TClaimSuccessfulContent) => {
  const { amount = 0 } = creditInfo;
  return (
    <div className="flex flexDirectionColumn alignItemsCenter">
      <Suspense fallback={null}>
        <LazySuccessLottie />
      </Suspense>
      <h3 className="marginTop-small2 text-align-center">
        Your {amount > 0 ? `${currencyFormatter(2).format(amount / 100)} ` : ''}credit is now available!
      </h3>
      <p className="p1 n900 text-align-center marginTop-small2 marginBottom-small2">
        Make sure you complete your order
        <br />
        using the same email that we sent the {CREDIT_LABEL} to.
      </p>
      <Button onClick={close} theme={BUTTON_THEMES.PRIMARY} medium className={styles.ctaButton}>
        Got it!
      </Button>
    </div>
  );
};

const ClaimStoreCreditsModal = () => {
  const router = useRouter();
  const { error } = useNotice();

  const [isOpen, setIsOpen] = useState(false);
  const [hasCheckedCredit, setHasCheckedCredit] = useState(false);
  const [claimSuccessful, setClaimSuccessful] = useState(true);
  const [creditInfo, setCreditInfo] = useState({ amount: 0 });
  const closeModal = () => setIsOpen(false);

  const { email: customerEmail, creditToken } = router.query;

  /* Mutation */
  const { mutateAsync } = useMutation({ mutationFn: credits.claim });

  /**
   * Read url params and claim credit using the provided email and token.
   */
  useEffect(() => {
    (async () => {
      if (!hasCheckedCredit && customerEmail && creditToken) {
        setHasCheckedCredit(true);

        const response = await mutateAsync({
          credit: {
            source: 'hellotech',
            email: (customerEmail as string).replace(/ /g, '+'),
            token: creditToken,
          },
        });

        if (response && !response.err) {
          const { data = {} } = response;
          setCreditInfo(data);
          setClaimSuccessful(true);
          setIsOpen(true);
        } else {
          const { errors = [] } = response.data || {};
          if (errors.includes('Email already associated with a credit')) {
            setClaimSuccessful(false);
            setIsOpen(true);
          } else {
            error(`${CREDIT_LABEL} could not be applied.`);
          }
          Logger('ClaimStoreCreditsModal')(errors.join(', '));
        }
      }
    })();
  }, [hasCheckedCredit, customerEmail, creditToken, error, mutateAsync]);

  const modalProps = {
    success: {
      hide: closeModal,
      headerClasses: styles.hideModalHeader,
      childrenClassName: styles.modalClass,
    },
    failure: {
      headerClasses: '',
      hide: closeModal,
      header: `${CREDIT_LABEL} already claimed`,
      footerElement3: (
        <Button onClick={closeModal} theme={BUTTON_THEMES.PRIMARY} medium inlineBlock>
          OK
        </Button>
      ),
    },
  };

  return (
    <>
      {claimSuccessful ? (
        <Modal isVisible={isOpen} {...modalProps.success}>
          <ClaimSuccessfulContent creditInfo={creditInfo} close={closeModal} />
        </Modal>
      ) : (
        <Modal isVisible={isOpen} {...modalProps.failure}>
          <p className="p1 marginTop-small2">You’ve already claimed your {CREDIT_LABEL}. Book a service and it will be automatically applied!</p>
        </Modal>
      )}
    </>
  );
};

export default ClaimStoreCreditsModal;
